import { upperFirst } from "lodash";
import axios from "axios";
import { SdkError, ERROR_CODES } from "@signaloid/cloud-compute-engine-client";
import { TaskStatistics, TaskStatus, TaskStatusE } from "@/types/api/tasks";
import { isTaskSuccess, isTaskFail } from "@/types/api/tasks";
import {
	Core,
	CoreDependencyTracking,
	CoreDependencyTrackingE,
	CoreMicroarchitectureE,
	CoreSpecs,
} from "@/types/api/cores";
import {
	ImageV1Content,
	ImageV2Content,
	ImageV3Content,
	OutputFetchErrorInfo,
	OutputFetchErrorKind,
	OutputFetchErrorKindE,
	OutputFetchTarget,
	RemoteAccessState,
	TransformedTabContent,
} from "@/types/general";
import { isLocalMountConfig, LocalMountConfig } from "@/types/api/dataSources";
import { forgetAnalyticsUser } from "@/plugins/identify";
import moment from "moment";
import { monitoringCaptureError } from "@/plugins/monitoring";
import { CORE_NAMES_TO_DESCRIPTION } from "@/types/utilities";
import { BuildStatus, BuildStatusE, BuildStatusTransition } from "@/types/api/builds";

export function getDesignToken(name: string, styles = getComputedStyle(document.documentElement)): string {
	return styles.getPropertyValue(name).trim();
}

export function toStringWithMagnitudeSuffix(
	value: number | undefined | null,
	suffixArray: string[],
	separator: string = "\u0020",
) {
	if (value === undefined || value === null) {
		return "--";
	}
	let suffixArrayIndex = 0;
	while (value >= 1000 && suffixArrayIndex < suffixArray.length - 1) {
		value = value / 1000;
		suffixArrayIndex++;
	}
	const suffixToShow = suffixArray[Math.min(suffixArrayIndex, suffixArray.length - 1)];
	return `${value.toFixed(0)}${suffixToShow ? separator : ""}${suffixToShow}`;
}

export function toStringWithMagnitude(value: number | undefined | null, separator = "\u0020") {
	const suffixArray = ["", "thousand", "million", "billion"];
	return toStringWithMagnitudeSuffix(value, suffixArray, separator);
}
export function toStringWithByteMagnitude(value: number | undefined | null, separator = "\u0020") {
	const suffixArray = ["", "KB", "MB", "GB", "TB"];
	return toStringWithMagnitudeSuffix(value, suffixArray, separator);
}
export function toStringWithSIMagnitude(value: number | undefined | null, separator = "\u0020") {
	const suffixArray = ["", "K", "M", "G"];
	return toStringWithMagnitudeSuffix(value, suffixArray, separator);
}
export function toStringWithDurationMagnitude(value: number | undefined | null, separator = " ") {
	if (value === undefined || value === null) {
		return "--";
	}
	const durationUnits = [
		{ suffix: "ms", nextUnitAt: 1000 },
		{ suffix: "s", nextUnitAt: 60 },
		{ suffix: "min", nextUnitAt: 60 },
		{ suffix: "h", nextUnitAt: Infinity },
	];
	let unitIndex = 0;
	while (value >= durationUnits[unitIndex].nextUnitAt) {
		value = value / durationUnits[unitIndex].nextUnitAt;
		unitIndex++;
	}
	return `${Number(value.toFixed(1))}${separator}${durationUnits[unitIndex].suffix}`;
}

export function usDurationToString(durationInUS: number | undefined | null) {
	if (durationInUS === undefined || durationInUS === null) {
		return "---";
	}

	const milliseconds = Math.trunc(durationInUS / 1000);
	const microSeconds = durationInUS % 1000;

	let formattedString = msDurationToString(milliseconds);

	if (microSeconds > 0) {
		formattedString += `${microSeconds.toFixed(0)} \u03BCs`;
	}

	return formattedString;
}

export function msDurationToString(durationInMS: number | undefined | null) {
	if (durationInMS === undefined || durationInMS === null) {
		return "---";
	}

	const durationObject = moment.duration(durationInMS);

	if (!durationObject.isValid()) {
		return "---";
	}

	let formattedString = "";

	if (durationObject.asDays() > 1) {
		formattedString += `${Math.ceil(durationObject.asDays() - 1)} day${durationObject.asDays() > 2 ? "s" : ""} `;
	}

	if (durationObject.hours() > 0) {
		formattedString += `${Math.ceil(durationObject.hours())} h `;
	}

	if (durationObject.minutes() > 0) {
		formattedString += `${Math.ceil(durationObject.minutes())} m `;
	} else if (formattedString.length > 0) {
		formattedString += `0 m `;
	}

	if (durationObject.seconds() > 0) {
		formattedString += `${Math.ceil(durationObject.seconds())} s `;
	} else if (formattedString.length > 0) {
		formattedString += `0 s `;
	}

	if (durationObject.milliseconds() > 0) {
		formattedString += `${Math.ceil(durationObject.milliseconds())} ms `;
	} else if (formattedString.length > 0) {
		formattedString += `0 ms `;
	}

	return formattedString;
}

export function numberLessThanTenToWords(value: number | undefined | null) {
	if (value === null) {
		return "";
	}
	// undefined is unlimited resource
	if (value === undefined) {
		return "unlimited";
	}
	switch (value) {
		case 9:
			return "nine";
		case 8:
			return "eight";
		case 7:
			return "seven";
		case 6:
			return "six";
		case 5:
			return "five";
		case 4:
			return "four";
		case 3:
			return "three";
		case 2:
			return "two";
		case 1:
			return "one";
		case 0:
			return "zero";
		default:
			return value.toString();
	}
}

export function sleep(ms: number) {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

const BuildStatusToColorMap = {
	[BuildStatusE.Pending]: "secondary",
	[BuildStatusE.Initialising]: "info",
	[BuildStatusE.Accepted]: "info",
	[BuildStatusE.InProgress]: "info",
	[BuildStatusE.Completed]: "success",
	[BuildStatusE.Cancelled]: "secondary",
	[BuildStatusE.Stopped]: "error",
	[BuildStatusE.Rescheduled]: "warning",
	[BuildStatusE.Error]: "error",
	[BuildStatusE.TierLimited]: "info",
} as const;

export function buildStatusColor(status: BuildStatus | undefined): string {
	return mapStatusToColor(status, BuildStatusToColorMap);
}

export const TaskStatusToColorMap = {
	[TaskStatusE.Pending]: "secondary",
	[TaskStatusE.NotStarted]: "secondary",
	[TaskStatusE.Initialising]: "info",
	[TaskStatusE.Accepted]: "info",
	[TaskStatusE.InProgress]: "info",
	[TaskStatusE.Completed]: "success",
	[TaskStatusE.Cancelled]: "secondary",
	[TaskStatusE.Stopped]: "error",
	[TaskStatusE.Rescheduled]: "warning",
	[TaskStatusE.NotFoundError]: "error",
	[TaskStatusE.Forbidden]: "error",
	[TaskStatusE.Error]: "error",
	[TaskStatusE.UpgradeAccount]: "info",
	[TaskStatusE.TierLimited]: "info",
} as const;

export function taskStatusColor(status: TaskStatus | undefined): string {
	return mapStatusToColor(status, TaskStatusToColorMap);
}

export function taskStatusIcon(status: TaskStatus | undefined) {
	if (status === undefined) {
		return "";
	} else if (status == TaskStatusE.TierLimited || status == TaskStatusE.UpgradeAccount) {
		return "info";
	} else if (status == TaskStatusE.Pending || status == TaskStatusE.Accepted) {
		return "mdi-tray-plus";
	} else if (status == TaskStatusE.Initialising) {
		return "mdi-database-sync";
	} else if (status == TaskStatusE.InProgress) {
		return "mdi-memory";
	} else if (status == TaskStatusE.Completed) {
		return "mdi-check";
	} else if (status == TaskStatusE.Stopped || status == TaskStatusE.Cancelled) {
		return "mdi-alert";
	} else if (isTaskSuccess(status)) {
		return "";
	} else if (isTaskFail(status)) {
		return "";
	} else {
		return "";
	}
}

export function projectSourceIcon(projectType: string) {
	switch (projectType) {
		case "SourceCode":
			return "mdi-script-text-play";
		case "Repository":
			return "mdi-book";
		// case TaskTypeE.Pipeline:
		// 	return "mdi-transit-connection-variant";
		// case TaskTypeE.Module:
		// 	return "mdi-package";
		// case "Schedule":
		// 	return "mdi-calendar-clock";
		// case TaskTypeE.Input:
		// 	return "mdi-import";
		default:
			return "mdi-progress-question";
	}
}

export function filterEveryNValue(inputArray: any[], n: number) {
	return inputArray.map((v, i, arr) => {
		if (i == 0 || i == arr.length - 1 || i % n == 0) {
			return v;
		} else {
			return "";
		}
	});
}

function findTreeMidpoints(inputArray: any[], l: number): string[] {
	const midpoint = Math.floor(inputArray.length / 2);
	if (l == 0 || inputArray.length < 2) {
		return [inputArray[midpoint]];
	} else {
		l -= 1;
		return [
			...findTreeMidpoints(inputArray.slice(0, midpoint), l),
			inputArray[midpoint],
			...findTreeMidpoints(inputArray.slice(midpoint), l),
		];
	}
}

export function symmetricFilter(inputArray: any[], depth: number): any[] {
	// @ts-ignore FIXME:
	const output = ["" * inputArray.length];
	const filteredValues = [inputArray[0], ...findTreeMidpoints(inputArray, depth), inputArray[inputArray.length - 1]];

	// construct label array
	filteredValues.map((displayValue) => {
		output[
			inputArray.findIndex((originalValue) => {
				return originalValue == displayValue;
			})
		] = displayValue;
	});

	return output;
}

export function convertLocalCoreObjectToRemoteFormat(coreConfig: Core) {
	//* Deprecated
	type OldCore = {
		Id: string;
		Name: string;
		Class: string;
		MemorySize: number;
		Microarchitecture: string;
		DependencyTracking: string;
		TotalUsage: number;
		AvailableUsage: number;
		Immutable: boolean;
		Disabled: boolean;
		ReferencePrecision: number;
		AutocorrelationTrackingEnabled: boolean;
		Editable: Boolean;
	};

	// PATCH: change keys to PascalCase before saving
	// eslint-disable-next-line prefer-const
	let newCoreConfig: Partial<Omit<Core, keyof OldCore> & OldCore> = {};

	for (const key in coreConfig) {
		if (Object.prototype.hasOwnProperty.call(coreConfig, key)) {
			// @ts-ignore FIXME:
			newCoreConfig[upperFirst(key) as keyof typeof newCoreConfig] = coreConfig[key as keyof typeof coreConfig];
		}
	}
	// PATCH: change user core id key from "Id" to "CoreID"
	newCoreConfig.CoreID = newCoreConfig.Id ?? newCoreConfig.CoreID;

	// PATCH: set core precision key if reference core
	if (
		newCoreConfig.Microarchitecture === CoreMicroarchitectureE.Reference &&
		newCoreConfig.ReferencePrecision &&
		newCoreConfig.Precision !== newCoreConfig.ReferencePrecision
	) {
		newCoreConfig.Precision = newCoreConfig.ReferencePrecision;
	}

	// PATCH: set core dependency tracking to string
	if (!newCoreConfig.DependencyTracking) {
		if (
			newCoreConfig.Microarchitecture === CoreMicroarchitectureE.Reference ||
			newCoreConfig.Microarchitecture === CoreMicroarchitectureE.Bypass
		) {
			newCoreConfig.DependencyTracking = CoreDependencyTrackingE.None;
		} else {
			newCoreConfig.DependencyTracking =
				newCoreConfig.AutocorrelationTrackingEnabled !== undefined
					? CoreDependencyTrackingE.Basic
					: CoreDependencyTrackingE.None;
		}
	}

	// PATCH: remove unused keys
	delete newCoreConfig.Id;
	delete newCoreConfig.AutocorrelationTrackingEnabled;
	delete newCoreConfig.Editable;
	delete newCoreConfig.ReferencePrecision;
	delete newCoreConfig.TotalUsage;
	delete newCoreConfig.AvailableUsage;

	return newCoreConfig;
}

// @ts-ignore FIXME:
export function convertLocalRepositoryObjectToRemoteFormat() {
	// TODO::
}

// @ts-ignore FIXME:
export function convertLocalPipelineObjectToRemoteFormat() {
	// TODO::
}

// @ts-ignore FIXME:
export function changeKeysToPascalCase(obj) {
	const newObj: { [key: string]: any } = {};
	for (const key in obj) {
		newObj[upperFirst(key)] = obj[key];
	}
	return newObj;
}

export function repoFullNameFromUrl(url: string | undefined | null): string {
	if (!url) return "";
	// Handles both HTTPS (github.com/owner/repo) and SSH (git@github.com:owner/repo) forms,
	// and trims the optional `.git` suffix.
	return url.replace(/\.git$/, "").replace(/^.*github\.com[/:]/, "");
}

export function repoShortNameFromUrl(url: string | undefined | null): string {
	const slug = repoFullNameFromUrl(url);
	return slug ? (slug.split("/").pop() ?? "") : "";
}

/*
	- Insures 32 alphanumerical chars are after val_
	- Insures 40 hex characters are after Ux
	- Insures that we correctly parse positive and negative floats, ints particle values
	- Matches scientific notations.
	- Matches signed nan and inf values in any case.
*/
export function transformTabContent(content: string, moreContentComing = false): TransformedTabContent[] {
	const startTime = performance.now();

	const results: TransformedTabContent[] = [];
	if (!content) {
		console.log(`Performance: ${performance.now() - startTime}ms`);
		return results;
	}

	let lastIndex = 0;
	const floatRegex = /([-+]?([0-9]*\.?[0-9]+([eE][-+]?[0-9]+)?|nan|inf))\b$/i;
	const trailingUxRegex = /U(?:x[0-9a-fA-F]*)?$/;
	const patterns = [
		{ regex: /<ValueID>(.*?)<\/ValueID>/g, identifier: "ValueID" },
		{ regex: /(Ux[0-9a-fA-F]{40,})\b/g, identifier: "UxString" },
	];

	function addTextIfNeeded(text: string, isLastSegment = false) {
		if (text || isLastSegment) {
			results.push({ type: "text", body: text });
		}
	}

	function addResult(prefix: string, value: string, identifier: string, identifierValue: string) {
		addTextIfNeeded(prefix);
		if (value) {
			results.push({
				type: "image",
				version: "v4",
				body: {
					Value: value,
					[identifier]: identifierValue,
					PlotLoading: true,
					ImageURL: "",
				},
			});
		}
	}

	let scannedContent = content;
	if (moreContentComing && trailingUxRegex.test(content)) {
		scannedContent = content.replace(trailingUxRegex, "").replace(floatRegex, "");
	}

	for (const { regex, identifier } of patterns) {
		regex.lastIndex = 0; // Reset regex index
		let match;
		while ((match = regex.exec(scannedContent)) !== null) {
			const segment = scannedContent.substring(lastIndex, match.index);
			const floatMatch = segment.match(floatRegex);
			const value = floatMatch ? floatMatch[0] : "";
			const prefix = floatMatch ? segment.substring(0, floatMatch.index) : segment;

			addResult(prefix, value, identifier, match[1]);
			lastIndex = regex.lastIndex;
		}
	}

	if (lastIndex < scannedContent.length) {
		addTextIfNeeded(scannedContent.substring(lastIndex), true);
	}

	console.log(`Performance: ${performance.now() - startTime}ms`);
	return results;
}

export function parseDDP0x42String(xmlString: string): ImageV1Content {
	// Parse plot v 1 (DistributionalDataPrint0x42)
	// - text followed by presigned URL within the XML tags

	const extractedImageURL = xmlString
		.replace("<DistributionalDataPrint0x42>", "")
		.replace("</DistributionalDataPrint0x42>", "")
		.trim();

	return {
		type: "image",
		version: "v1",
		body: { ImageURL: extractedImageURL },
	};
}
export function parseDDP0x45String(xmlString: string): ImageV2Content {
	// Parse plot v2 (DistributionalDataPrint0x45)
	// - json inside XML tags with float value and a presigned URL for the value
	const newVal = xmlString
		.replace("<DistributionalDataPrint0x45>", "")
		.replace("</DistributionalDataPrint0x45>", "")
		.trim();
	/*
	 *	According to the format specification, newVal should be
	 *	valid JSON.
	 */

	try {
		const parsedJson = JSON.parse(newVal, (k, v) => {
			switch (k) {
				case "ImageURL":
					/*
					 *	ImageURL is URI-encoded (e.g., '&' appears as '\u0026').
					 */
					return decodeURI(v);

				default:
					return v;
			}
		});
		return {
			type: "image",
			version: "v2",
			body: parsedJson,
		};
	} catch (error) {
		monitoringCaptureError(error, "Parse Ux45 string");
		if (error instanceof SyntaxError) {
			/*
			 *	Error was because of invalid JSON input syntax.
			 */
			return {
				type: "image",
				version: "v2",
				body: {
					Value: "???????",
					ImageURL: undefined,
				},
			};
		} else if (error instanceof URIError) {
			/*
			 *	Error was because of invalid URI input syntax for key "ImageURL".
			 */
			return {
				type: "image",
				version: "v2",
				body: {
					Value: "???????",
					ImageURL: undefined,
				},
			};
		}
		/*
		 *	Error is of unaccounted-for cause.
		 */
		return {
			type: "image",
			version: "v2",
			body: {
				Value: "???????",
				ImageURL: undefined,
			},
		};
	}
}

export function parseDDP0x46String(xmlString: string): ImageV3Content {
	// Parse plot v3 (DistributionalDataPrint0x46)
	// - json inside XML tags with float value and a value ID
	const newVal = xmlString
		.replace("<DistributionalDataPrint0x46>", "")
		.replace("</DistributionalDataPrint0x46>", "")
		.trim();
	/*
	 *	According to the format specification, newVal should be
	 *	valid JSON.
	 */

	try {
		const parsedJson = JSON.parse(newVal);
		parsedJson.ImageURL = "";
		parsedJson.PlotLoading = true;
		return {
			type: "image",
			version: "v3",
			body: parsedJson,
		};
	} catch (error) {
		monitoringCaptureError(error, "Parse Ux46 string");
		/*
		 *	Error is of unaccounted-for cause.
		 */
		return {
			type: "image",
			version: "v3",
			body: {
				Value: "???????",
				ValueID: undefined,
			},
		};
	}
}

// Marketing-attribution props for the user_account_created event, set on the
// landing in Login.vue. Email and Google signup paths capture the same bag.
export function signupAttributionProps() {
	return {
		utm_source: sessionStorage.getItem("ph_utm_source"),
		utm_medium: sessionStorage.getItem("ph_utm_medium"),
		utm_campaign: sessionStorage.getItem("ph_utm_campaign"),
		referrer_domain: sessionStorage.getItem("ph_referrer_domain"),
		came_from_website: sessionStorage.getItem("ph_came_from_website") === "true",
	};
}

// Per-user home alert dismissals are keyed `home:alertDismissals:<userId>`. The
// prefix is exported so the Home view builds its key from the same source and
// clearLocalState can preserve these entries across a logout (a dismissed alert
// stays dismissed after logging back in).
export const HOME_ALERT_DISMISSALS_PREFIX = "home:alertDismissals:";

const PRESERVED_LOCAL_STORAGE_PREFIXES = [HOME_ALERT_DISMISSALS_PREFIX];

export function clearLocalState() {
	// Wipe everything except the per-user keys that must outlive a logout.
	const toRemove: string[] = [];
	for (let i = 0; i < localStorage.length; i++) {
		const key = localStorage.key(i);
		if (key && !PRESERVED_LOCAL_STORAGE_PREFIXES.some((prefix) => key.startsWith(prefix))) {
			toRemove.push(key);
		}
	}
	for (const key of toRemove) {
		localStorage.removeItem(key);
	}
	// reset analytics config
	// @ts-ignore: Element implicitly has an 'any' type because type 'typeof globalThis' has no index signature.
	globalThis.analytics?.reset();
	forgetAnalyticsUser();
}

export function isAbsoluteURL(url: string) {
	try {
		// new URL(url) without a base throws for relative paths,
		// letting us distinguish them from absolute URLs.
		// Accepts only http/https to block javascript:, data:, etc.
		const parsed = new URL(url);
		return parsed.protocol === "http:" || parsed.protocol === "https:";
	} catch {
		return false;
	}
}

export function shortenID(id: string, shortLength = 7) {
	const prefix = "tsk_";
	return id.slice(0, shortLength + prefix.length);
}

export function formatElapsedMmSs(ms: number): string {
	const totalSec = Math.max(0, Math.floor(ms / 1000));
	const min = Math.floor(totalSec / 60);
	const sec = totalSec % 60;
	return `${min}:${sec.toString().padStart(2, "0")}`;
}

export function coreNameFromSpecs(
	specs: CoreSpecs | undefined,
	defaultCores: Core[],
	customLabel = "Custom Core",
): string {
	if (!specs) return customLabel;
	const translatedSpecs = translateLegacyCoreSpecs(specs);
	const match = defaultCores.find(
		(c) =>
			c.Class === translatedSpecs.Class &&
			c.Precision === translatedSpecs.Precision &&
			c.Microarchitecture === translatedSpecs.Microarchitecture &&
			c.DependencyTracking === translatedSpecs.DependencyTracking,
	);
	return match?.Name ?? customLabel;
}

type LegacyCoreSpecFields = {
	Microarchitecture?: string;
	DependencyTracking?: string;
	CorrelationTracking?: string;
};

export const LEGACY_ATLAS_MICROARCHITECTURE = "Atlas";

const DEPENDENCY_TRACKING_BY_LEGACY_CORRELATION_TRACKING: Record<string, CoreDependencyTracking> = {
	autocorrelation: CoreDependencyTrackingE.Basic,
	disable: CoreDependencyTrackingE.None,
};

function dependencyTrackingForLegacy(
	correlationTracking: string,
	microarchitecture: string | undefined,
): CoreDependencyTracking | undefined {
	const dependencyTracking =
		DEPENDENCY_TRACKING_BY_LEGACY_CORRELATION_TRACKING[String(correlationTracking).toLowerCase()];
	if (dependencyTracking === CoreDependencyTrackingE.None && microarchitecture === CoreMicroarchitectureE.Jupiter) {
		return CoreDependencyTrackingE.Advanced;
	}
	return dependencyTracking;
}

export function translateLegacyCoreSpecs<T extends LegacyCoreSpecFields>(specs: T): T {
	const translated: LegacyCoreSpecFields = { ...specs };
	if (translated.Microarchitecture === LEGACY_ATLAS_MICROARCHITECTURE) {
		translated.Microarchitecture = CoreMicroarchitectureE.Rome;
	}
	if (translated.CorrelationTracking === undefined) {
		return translated as T;
	}

	const dependencyTracking = dependencyTrackingForLegacy(
		translated.CorrelationTracking,
		translated.Microarchitecture,
	);
	if (dependencyTracking === undefined) {
		return translated as T;
	}
	delete translated.CorrelationTracking;
	translated.DependencyTracking ??= dependencyTracking;
	return translated as T;
}

export function sortArryOfObjectByKey<T>(arrayOfObjects: T[], key: keyof T) {
	// if (typeof key of  T[key] === "number")
	return arrayOfObjects.sort((a, b) => {
		// @ts-ignore FIXME: check for numerics
		return a[key] - b[key];
	});
}

export function byNewestFirst<T>(timestamp: (item: T) => number, id: (item: T) => string) {
	// Two Infinity timestamps subtract to NaN, which falls through to the ID tie-break
	return (a: T, b: T) => timestamp(b) - timestamp(a) || id(a).localeCompare(id(b));
}

export function mergeByID<T>(existing: T[], fetched: T[], id: (item: T) => string): T[] {
	// A Map keeps first insertion order while a later set() replaces the value
	return Array.from(new Map(existing.concat(fetched).map((item): [string, T] => [id(item), item])).values());
}

export function parseTaskStatsObjectToStatsTabString(taskStats: undefined | TaskStatistics): string {
	let statsTabText = "";

	if (taskStats?.ProcessorTime) {
		statsTabText += `C0 execution time:  ${usDurationToString(taskStats.ProcessorTime * 1000_000)}\n`;
	}

	if (taskStats?.ExecutionTimeInMilliseconds) {
		statsTabText += `Execution time counted against your monthly limit: ${usDurationToString(taskStats.ExecutionTimeInMilliseconds * 1000)}\n`;
	}

	return statsTabText;
}

export function base64Encode(str) {
	return Buffer.from(str).toString("base64");
}
export function base64Decode(str) {
	// From https://stackoverflow.com/questions/8936984/uint8array-to-string-in-javascript
	return new TextDecoder().decode(Buffer.from(str, "base64"));
}

// Generic wait for function based on https://stackoverflow.com/questions/7193238/wait-until-a-condition-is-true
export function waitFor(conditionFunction) {
	const poll = (resolve) => {
		if (conditionFunction()) resolve();
		else setTimeout(() => poll(resolve), 400);
	};

	return new Promise(poll);
}

export function parseMountConfigString(mountConfigString?: string): undefined | null | LocalMountConfig {
	if (mountConfigString === null) {
		return null;
	}
	if (!mountConfigString) {
		return undefined;
	}

	if (mountConfigString === "undefined") {
		// the string "undefined"  => data sources not set
		return undefined;
	} else if (mountConfigString === "null") {
		// the string "null"  => set to no data sources
		return null;
	} else {
		// some other string => possibly a mount config
		try {
			// try to parse it
			const parsedEditorDataSourcesFromDB = JSON.parse(mountConfigString);
			if (isLocalMountConfig(parsedEditorDataSourcesFromDB)) {
				// if the parsed object is a valid mount config return it
				return parsedEditorDataSourcesFromDB;
			} else {
				throw new Error("Error parsing the string mount config");
			}
		} catch (error) {
			monitoringCaptureError(error, "Parse mount config string");
			// if parsing error... return undefined
			return undefined;
		}
	}
}

export function checkCoreRequirements(coreToCheck, minCore) {
	const requirementMatch = {};
	for (const key in minCore) {
		// check if requirement satisfied for numerical constraints
		if (typeof minCore[key] == "number") {
			requirementMatch[key] = coreToCheck[key] >= minCore[key];
		} else {
			// check if requirement satisfied for non numerical constraints
			if (["Class", "Microarchitecture", "DependencyTracking"].includes(key)) {
				requirementMatch[key] = coreToCheck[key] === minCore[key];
			}
		}
	}
	return requirementMatch;
}

export function secondsSinceEpochToDateString(timestamp: number) {
	//moment requires timestamp in milliseconds
	return moment(timestamp * 1000).calendar(null, {
		sameDay: "[Today]",
		nextDay: "[Tomorrow]",
		nextWeek: "dddd",
		lastDay: "[Yesterday]",
		lastWeek: "[Last] dddd",
		sameElse: "DD/MM/YYYY",
	});
}

export function millisecondsSinceEpochToDateString(timestamp: number) {
	//moment requires timestamp in milliseconds
	return moment(timestamp).calendar(null, {
		sameDay: "[Today]",
		nextDay: "[Tomorrow]",
		nextWeek: "dddd",
		lastDay: "[Yesterday]",
		lastWeek: "[Last] dddd",
		sameElse: "DD/MM/YYYY",
	});
}
function isMilliseconds(timestamp) {
	return timestamp > 1e11; // If greater than 100 billion, assume milliseconds
}

export function convertToMilliseconds(timestamp) {
	return isMilliseconds(timestamp) ? timestamp : timestamp * 1000;
}

export function formatDuration(startTimestamp, endTimestamp, secondsOnly = false) {
	const AsTimeStamp_19900101 = 631152000;

	if (!startTimestamp || !endTimestamp) {
		return "---";
	}

	const startMs = convertToMilliseconds(startTimestamp);
	const endMs = convertToMilliseconds(endTimestamp);

	if (startTimestamp <= AsTimeStamp_19900101 || endTimestamp <= AsTimeStamp_19900101) {
		return "---";
	}

	const finishTime = moment(endMs);
	const startTime = moment(startMs);

	if (finishTime.isValid() && startTime.isValid()) {
		const duration = finishTime.diff(startTime);
		if (duration < 1000) {
			return secondsOnly ? "< 1s" : `${duration}ms`;
		} else if (duration < 60000) {
			return `${Math.floor(duration / 1000)}s` + (secondsOnly ? "" : ` ${duration % 1000}ms`);
		} else {
			return `${Math.floor(duration / 60000)}m ${Math.floor((duration % 60000) / 1000)}s`;
		}
	} else {
		return "---";
	}
}

export const ContactSignaloidSupportMessage =
	"If this error persists, please contact support at developer-support@signaloid.com.";

export function coreDescriptionString(coreName: string, firstSentence: boolean = false): string {
	const coreNameLowerCase = coreName.toLowerCase();
	const description = CORE_NAMES_TO_DESCRIPTION[coreNameLowerCase];
	if (!description) {
		console.warn("Unkown core name", coreNameLowerCase);
		return "";
	}
	if (firstSentence) {
		return description.split(".")[0] + ".";
	} else {
		return description;
	}
}

/**
 * Parsed build error information
 */
export type ParsedBuildError = {
	/** Error code (e.g., "ErrRepositoryClone") */
	code: string | null;
	/** Human-readable error message without the error code prefix */
	message: string | null;
};

/**
 * Parses build error information from status transitions
 * Kea-cloud errors are formatted as `ErrXXX: Error message`, where `ErrXXX` is an enum-like identifier.
 * This function extracts both the error code and the human-readable message.
 *
 * @param statusTransitions - Array of build status transitions
 * @param currentStatus - The current status of the build
 * @returns An object containing the error code and message, or null values if no error found
 *
 * @example
 * const error = parseBuildError(build.StatusTransitions, build.Status);
 * if (error.code === "ErrRepositoryClone") {
 *   console.log("Clone failed:", error.message);
 * }
 */
export function parseBuildError(
	statusTransitions: BuildStatusTransition[] | undefined,
	currentStatus: BuildStatus,
): ParsedBuildError {
	if (!statusTransitions) {
		return { code: null, message: null };
	}

	const statusTransition = statusTransitions.find((st) => st.Status === currentStatus);
	if (!statusTransition?.Message) {
		return { code: null, message: null };
	}

	// Extract error code from format "ErrXXX: Error message"
	const match = statusTransition.Message.match(/^(Err\w+):/);
	const code = match ? match[1] : null;

	// Extract message after "ErrXXX: "
	let message = statusTransition.Message;
	if (message.includes(":")) {
		const messageParts = message.split(":");
		message = messageParts.slice(1).join(":").trim();
	}

	return { code, message };
}

export const MINIMUM_RETRY_FEEDBACK_MS = 400;

export async function withMinimumDuration<T>(work: Promise<T>, minimumMs: number): Promise<T> {
	const [outcome] = await Promise.allSettled([work, sleep(minimumMs)]);
	if (outcome.status === "rejected") {
		throw outcome.reason;
	}
	return outcome.value;
}

/** Downloads an in-memory blob through a temporary object URL. */
export function triggerBlobDownload(blob: Blob, fileName: string) {
	const objectURL = window.URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = objectURL;
	link.setAttribute("download", fileName);
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	window.URL.revokeObjectURL(objectURL);
}

/**
 * Retry an async operation with exponential backoff
 * @param operation - The async operation to retry
 * @param delays - Array of delays in milliseconds between retries (e.g., [500, 1000, 2000])
 * @param errorMessage - Error message to throw if all attempts fail
 * @returns Promise resolving to the operation result
 */
export async function retryWithBackoff<T>(
	operation: () => Promise<T>,
	delays: number[] = [500, 1000, 2000],
	errorMessage: string = "Operation failed after multiple attempts",
): Promise<T> {
	let lastError: unknown;
	for (let attempt = 0; attempt < delays.length; attempt++) {
		try {
			return await operation();
		} catch (error) {
			lastError = error;
			if (attempt === delays.length - 1) {
				throw new Error(errorMessage, { cause: error });
			}
			await new Promise((resolve) => setTimeout(resolve, delays[attempt]));
		}
	}
	throw new Error(errorMessage, { cause: lastError });
}

function unwrapCause(err: unknown): unknown {
	return err instanceof Error && err.cause !== undefined ? err.cause : err;
}

const FETCH_ERROR_MESSAGES: Record<
	OutputFetchErrorKind,
	(target: OutputFetchTarget, retryAfterSeconds?: number) => string
> = {
	[OutputFetchErrorKindE.Auth]: () => "Session expired — please sign in again.",
	[OutputFetchErrorKindE.Forbidden]: (target) =>
		`Couldn't load output — this ${target} belongs to a different account.`,
	[OutputFetchErrorKindE.NotFound]: (target) => `No outputs are available for this ${target}.`,
	[OutputFetchErrorKindE.MissingResource]: (target) =>
		`We could not find this ${target}. It was probably deleted or the link is out of date. Open Builds and Tasks to see your current ${target}s.`,
	[OutputFetchErrorKindE.Timeout]: () => "Couldn't load output — the request took too long. Please try again.",
	[OutputFetchErrorKindE.RateLimit]: (_target, retryAfterSeconds) =>
		retryAfterSeconds
			? `Couldn't load output — rate limited. Retry in ${retryAfterSeconds} seconds.`
			: "Couldn't load output — rate limited. Please try again shortly.",
	[OutputFetchErrorKindE.Server]: () =>
		"Couldn't load output — we're having trouble right now. Please try again shortly.",
	[OutputFetchErrorKindE.Offline]: () => "You're offline. Check your connection and try again.",
	[OutputFetchErrorKindE.Network]: () => "Couldn't load output. Please try again in a moment.",
	[OutputFetchErrorKindE.Unknown]: () => "Couldn't load output.",
};

type Severity = "fatal" | "error" | "warning" | "info";

/**
 * Policy per fetch-error kind: retryability, Sentry capture, and severity.
 * `severity: undefined` for `server` defers to monitoringCaptureError's built-in
 * 5xx → fatal/error mapping in `src/plugins/monitoring.ts`.
 */
const KIND_POLICY: Record<OutputFetchErrorKind, { retryable: boolean; capture: boolean; severity?: Severity }> = {
	[OutputFetchErrorKindE.Auth]: { retryable: false, capture: false },
	[OutputFetchErrorKindE.Forbidden]: { retryable: false, capture: true, severity: "warning" },
	[OutputFetchErrorKindE.NotFound]: { retryable: false, capture: true, severity: "warning" },
	[OutputFetchErrorKindE.MissingResource]: { retryable: false, capture: true, severity: "warning" },
	[OutputFetchErrorKindE.Timeout]: { retryable: true, capture: true, severity: "warning" },
	[OutputFetchErrorKindE.RateLimit]: { retryable: true, capture: true, severity: "warning" },
	[OutputFetchErrorKindE.Server]: { retryable: true, capture: true },
	[OutputFetchErrorKindE.Offline]: { retryable: true, capture: false },
	[OutputFetchErrorKindE.Network]: { retryable: true, capture: true, severity: "warning" },
	[OutputFetchErrorKindE.Unknown]: { retryable: true, capture: true, severity: "error" },
};

/**
 * HTTP status to fetch-error kind, shared by the axios and SDK error paths.
 * `undefined` means the status carries no verdict, so the caller falls back to
 * the transport-specific signals (error code, missing response).
 */
function kindFromStatus(status?: number): OutputFetchErrorKind | undefined {
	if (status === 401) return OutputFetchErrorKindE.Auth;
	if (status === 403) return OutputFetchErrorKindE.Forbidden;
	if (status === 404) return OutputFetchErrorKindE.NotFound;
	if (status === 408) return OutputFetchErrorKindE.Timeout;
	if (status === 429) return OutputFetchErrorKindE.RateLimit;
	if (status !== undefined && status >= 500) return OutputFetchErrorKindE.Server;
	return undefined;
}

/**
 * SDK error codes mapped to fetch-error kinds, for the errors the SDK raises
 * with no HTTP status: the auth codes thrown while signing a request, and the
 * realtime paths that reject with API_SERVER_ERROR. Codes without a row fall
 * back to Unknown, which is right for the config-error codes — they should
 * reach Sentry rather than read as a transient fetch failure.
 */
const SDK_CODE_TO_KIND: Record<string, OutputFetchErrorKind> = {
	[ERROR_CODES.AUTH_MISSING_TOKEN]: OutputFetchErrorKindE.Auth,
	[ERROR_CODES.AUTH_INVALID_TOKEN]: OutputFetchErrorKindE.Auth,
	[ERROR_CODES.AUTH_EXPIRED_TOKEN]: OutputFetchErrorKindE.Auth,
	[ERROR_CODES.AUTH_FAILED]: OutputFetchErrorKindE.Auth,
	[ERROR_CODES.API_UNAUTHORIZED]: OutputFetchErrorKindE.Auth,
	[ERROR_CODES.API_FORBIDDEN]: OutputFetchErrorKindE.Forbidden,
	[ERROR_CODES.API_NOT_FOUND]: OutputFetchErrorKindE.NotFound,
	[ERROR_CODES.API_SERVER_ERROR]: OutputFetchErrorKindE.Server,
};

function positiveSeconds(seconds: number | undefined): number | undefined {
	return seconds !== undefined && Number.isFinite(seconds) && seconds > 0 ? seconds : undefined;
}

function resolveErrorKind(err: unknown): { kind: OutputFetchErrorKind; retryAfterSeconds?: number } {
	if (err instanceof SdkError) {
		// Status first: the SDK collapses every 5xx onto one code and leaves 408
		// in its catch-all, so the code alone loses the timeout.
		const sdkKind = kindFromStatus(err.status) ?? SDK_CODE_TO_KIND[err.code] ?? OutputFetchErrorKindE.Unknown;
		return {
			kind: sdkKind,
			retryAfterSeconds:
				sdkKind === OutputFetchErrorKindE.RateLimit ? positiveSeconds(err.retryAfter) : undefined,
		};
	}
	if (!axios.isAxiosError(err)) {
		return { kind: OutputFetchErrorKindE.Unknown };
	}
	const code = err.code;
	if (code === "ECONNABORTED") {
		return { kind: OutputFetchErrorKindE.Timeout };
	}
	const kind = kindFromStatus(err.response?.status);
	if (kind === OutputFetchErrorKindE.RateLimit) {
		const retryAfterHeader = err.response?.headers?.["retry-after"];
		return { kind, retryAfterSeconds: positiveSeconds(retryAfterHeader ? Number(retryAfterHeader) : undefined) };
	}
	if (kind !== undefined) {
		return { kind };
	}
	if (!err.response || code === "ERR_NETWORK") {
		if (typeof navigator !== "undefined" && navigator.onLine === false) {
			return { kind: OutputFetchErrorKindE.Offline };
		}
		return { kind: OutputFetchErrorKindE.Network };
	}
	return { kind: OutputFetchErrorKindE.Unknown };
}

export function isTransientError(err: unknown): boolean {
	const { kind } = resolveErrorKind(err);
	return kind !== OutputFetchErrorKindE.Unknown && KIND_POLICY[kind].retryable;
}

export function recordListFetchError(
	error: unknown,
	status: RemoteAccessState,
	forbiddenMessage: string,
	transaction: string,
): void {
	if (error instanceof SdkError && error.code === ERROR_CODES.API_FORBIDDEN) {
		status.message = forbiddenMessage;
		status.error = false;
		return;
	}
	status.error = true;
	status.message = "";
	monitoringCaptureError(error, transaction);
}

/**
 * Marks a 404 from the resource lookup that gates an output fetch. The task or
 * build itself is gone, which is a different failure from its outputs being
 * unavailable, so the two get different copy.
 */
class MissingResourceError extends Error {
	constructor(cause: unknown) {
		super("Resource not found", { cause });
		this.name = "MissingResourceError";
	}
}

/**
 * Runs the resource lookup that gates an output fetch, tagging a 404 as a
 * missing resource. Every other failure propagates unchanged.
 */
export async function requireResource<T>(lookup: () => Promise<T>): Promise<T> {
	try {
		return await lookup();
	} catch (error) {
		const { kind } = resolveErrorKind(unwrapCause(error));
		throw kind === OutputFetchErrorKindE.NotFound ? new MissingResourceError(error) : error;
	}
}

export function classifyFetchError(err: unknown, target: OutputFetchTarget): OutputFetchErrorInfo {
	// The marker holds the original error as its cause, so check it before unwrapping.
	const resolved: { kind: OutputFetchErrorKind; retryAfterSeconds?: number } =
		err instanceof MissingResourceError
			? { kind: OutputFetchErrorKindE.MissingResource }
			: resolveErrorKind(unwrapCause(err));
	const { kind, retryAfterSeconds } = resolved;
	return {
		kind,
		message: FETCH_ERROR_MESSAGES[kind](target, retryAfterSeconds),
		retryable: KIND_POLICY[kind].retryable,
		retryAfterSeconds,
	};
}

export function reportOutputFetchError(
	err: unknown,
	info: OutputFetchErrorInfo,
	target: OutputFetchTarget,
	resourceID: string,
): void {
	const policy = KIND_POLICY[info.kind];
	if (!policy.capture) {
		return;
	}
	const tags = {
		errorKind: info.kind,
		target,
		resourceID,
	};
	const extras = { userMessage: info.message };
	const raw = unwrapCause(err);
	monitoringCaptureError(raw, `outputs.fetchFailed.${target}`, {
		severity: policy.severity,
		tags,
		extras,
	});
}

export function mergeAccessState(
	fetchState: RemoteAccessState | undefined,
	localState: RemoteAccessState,
): RemoteAccessState {
	return fetchState?.error ? fetchState : localState;
}

// Sources of a build/task error a status bar can surface, highest priority first.
export const ResourceErrorTierE = {
	Reason: "reason",
	OutputFetch: "output-fetch",
	Stuck: "stuck",
} as const;
export type ResourceErrorTier = (typeof ResourceErrorTierE)[keyof typeof ResourceErrorTierE];

export type ResourceError = {
	tier: ResourceErrorTier;
	message: string;
	severity: "error" | "warning" | "info";
	retryable: boolean;
	loading: boolean;
	link?: { text: string; href: string };
};
export type ResourceErrorView = ResourceError | null;

/** Highest-priority error to surface: reason → output-fetch → stuck. `kind` marks an output-fetch error. */
export function resolveResourceError(
	terminalMessage: string | null,
	accessState: RemoteAccessState | undefined,
	stuckMessage: string | null,
): ResourceErrorView {
	if (terminalMessage) {
		return {
			tier: ResourceErrorTierE.Reason,
			message: terminalMessage,
			severity: "error",
			retryable: false,
			loading: false,
		};
	}
	if (accessState?.error && accessState.message) {
		const isOutputFetch = accessState.kind !== undefined;
		return {
			tier: isOutputFetch ? ResourceErrorTierE.OutputFetch : ResourceErrorTierE.Reason,
			message: accessState.message,
			severity: isOutputFetch ? "warning" : "error",
			retryable: accessState.retryable ?? false,
			loading: accessState.loading,
			link: accessState.link,
		};
	}
	if (stuckMessage) {
		return {
			tier: ResourceErrorTierE.Stuck,
			message: stuckMessage,
			severity: "info",
			retryable: false,
			loading: false,
		};
	}
	return null;
}

/**
 * The output-fetch failure to surface alongside a failure reason, with retry.
 * Null unless a reason is present and the fetch failed with a real (non-NotFound)
 * error: with no reason the fetch error is the primary view instead, and a 404
 * next to a reason is redundant since the reason already explains the missing output.
 */
export function resolveFetchRetry(
	terminalMessage: string | null,
	accessState: RemoteAccessState | undefined,
): ResourceErrorView {
	if (!terminalMessage || !accessState?.error || !accessState.message) {
		return null;
	}
	if (accessState.kind === undefined || accessState.kind === OutputFetchErrorKindE.NotFound) {
		return null;
	}
	return {
		tier: ResourceErrorTierE.OutputFetch,
		message: accessState.message,
		severity: "warning",
		retryable: accessState.retryable ?? false,
		loading: accessState.loading,
	};
}

/** True when build and task show the same output-fetch error, so the two banners collapse into one. */
export function isDedupedFetchError(buildView: ResourceErrorView, taskView: ResourceErrorView): boolean {
	return (
		buildView?.tier === ResourceErrorTierE.OutputFetch &&
		taskView?.tier === ResourceErrorTierE.OutputFetch &&
		buildView.message === taskView.message
	);
}

/**
 * Wraps an output-fetch operation with the standard access-state lifecycle:
 * reset on entry, clear on success, populate kind/message/retryable on failure
 * and forward to Sentry via reportOutputFetchError. Re-throws the original error.
 */
export async function withOutputAccessState<T>(
	target: OutputFetchTarget,
	resourceID: string,
	setState: (state: RemoteAccessState) => void,
	fetch: () => Promise<T>,
): Promise<T> {
	setState({ loading: true, error: false, message: "" });
	try {
		const result = await fetch();
		setState({ loading: false, error: false, message: "" });
		return result;
	} catch (error) {
		const info = classifyFetchError(error, target);
		setState({
			loading: false,
			error: true,
			message: info.message,
			kind: info.kind,
			retryable: info.retryable,
		});
		reportOutputFetchError(error, info, target, resourceID);
		throw error;
	}
}

/**
 * Safe localStorage wrapper with JSON parsing and error handling
 * @param key - The localStorage key
 * @param defaultValue - Default value to return if parsing fails or key doesn't exist
 * @returns Parsed value or default value
 */
export function safeGetLocalStorage<T>(key: string, defaultValue: T): T {
	try {
		const item = localStorage.getItem(key);
		if (item === null) {
			return defaultValue;
		}
		return JSON.parse(item) as T;
	} catch (error) {
		monitoringCaptureError(error, `Failed to parse localStorage key: ${key}`);
		return defaultValue;
	}
}

/**
 * Safe localStorage setter with JSON stringification
 * @param key - The localStorage key
 * @param value - Value to store (will be JSON stringified)
 */
export function safeSetLocalStorage<T>(key: string, value: T): void {
	try {
		localStorage.setItem(key, JSON.stringify(value));
	} catch (error) {
		monitoringCaptureError(error, `Failed to set localStorage key: ${key}`);
	}
}

/**
 * Generic status to color mapper
 * @param status - The status value
 * @param statusMap - Map of status to color
 * @param defaultColor - Default color if status not found
 * @returns Color string
 */
export function mapStatusToColor<T extends string>(
	status: T | undefined,
	statusMap: Record<T, string>,
	defaultColor: string = "error",
): string {
	if (!status) {
		return defaultColor;
	}
	return statusMap[status] ?? defaultColor;
}

/**
 * Capitalizes the first character of a string.
 * @param value - Input string, or undefined.
 * @returns The string with its first character uppercased, or undefined if input is undefined.
 */
export function capitalize(value: string | undefined): string | undefined {
	return value ? value.charAt(0).toUpperCase() + value.slice(1) : value;
}
